import React, { useState } from 'react';
import { Banknote, CreditCard, FileText, Landmark, Clock, Wallet } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import { patchReceipt } from '../../lib/api';

const OPTIONS = [
  { value: 'cash', label: 'Cash', Icon: Banknote },
  { value: 'debit', label: 'Debit', Icon: Wallet },
  { value: 'credit_card', label: 'Credit Card', Icon: CreditCard },
  { value: 'cheque', label: 'Cheque', Icon: FileText },
  { value: 'bank_transfer', label: 'Bank Transfer', Icon: Landmark },
  { value: 'pay_later', label: 'Pay Later / Bill', Icon: Clock },
];

/**
 * Phase 25.3 — Segmented payment method picker.
 *
 * PATCHes `/api/receipts/{id}` with `payment_method` immediately on
 * selection, then invokes `onChanged(value)` so the parent (ReceiptDetail)
 * can re-run `publish-check` and update the Publish card inline.
 */
export default function PaymentMethodPicker({ receipt, onChanged }) {
  const { colors } = useTheme();
  const [value, setValue] = useState(receipt?.payment_method || '');
  const [saving, setSaving] = useState(false);

  const select = async (next) => {
    if (next === value) return;
    const prev = value;
    setValue(next);
    setSaving(true);
    try {
      await patchReceipt(receipt.id, { payment_method: next });
      onChanged?.(next);
    } catch (e) {
      setValue(prev);
      console.error('Failed to update payment_method:', e);
    } finally {
      setSaving(false);
    }
  };

  const isPayLater = value === 'pay_later';

  return (
    <div
      data-testid="payment-method-picker"
      style={{
        padding: 12, borderRadius: 10,
        background: colors.surface, border: `1px solid ${colors.border}`,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, marginBottom: 2 }}>
        Payment method
      </div>
      <div style={{ fontSize: 11.5, color: colors.textMuted, marginBottom: 10 }}>
        Required for QuickBooks publishing. Pay-Later publishes as a Bill.
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 6,
        }}
      >
        {OPTIONS.map(({ value: v, label, Icon }) => {
          const active = v === value;
          return (
            <button
              key={v}
              type="button"
              data-testid={`pm-opt-${v}`}
              onClick={() => select(v)}
              disabled={saving}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '8px 10px', borderRadius: 9,
                background: active ? colors.accentSoft : colors.bg,
                color: active ? colors.accent : colors.text,
                border: `1px solid ${active ? colors.accent : colors.border}`,
                fontSize: 12, fontWeight: 700,
                cursor: saving ? 'wait' : 'pointer', whiteSpace: 'nowrap',
              }}
            >
              <Icon size={13} />
              {label}
            </button>
          );
        })}
      </div>

      {isPayLater && (
        <div
          data-testid="pm-pay-later-callout"
          style={{
            marginTop: 10, padding: 9, borderRadius: 8,
            background: '#FFF4D6', color: '#8A5A00',
            fontSize: 12,
          }}
        >
          <strong>Bill mode:</strong> this receipt will publish as a QuickBooks{' '}
          <code>Bill</code> (Accounts Payable). A vendor name is required.
        </div>
      )}
    </div>
  );
}
